import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, Pencil, X, Phone, Mail, Package, FileText,
  Plus, Trash2, Clock, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TICKET_STATUSES = ['Open', 'In Progress', 'Ready to Dispatch', 'Cancelled', 'Void'];
const TASK_STATUSES = ['Open', 'In Progress', 'Done', 'Cancelled'];
const TASK_PRIORITIES = ['Low', 'Medium', 'High'];

const STATUS_STYLES = {
  'Open': 'bg-blue-100 text-blue-700 border-blue-200',
  'In Progress': 'bg-amber-100 text-amber-700 border-amber-200',
  'Ready to Dispatch': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Cancelled': 'bg-red-100 text-red-700 border-red-200',
  'Void': 'bg-slate-200 text-slate-600 border-slate-300',
  'Done': 'bg-green-100 text-green-700 border-green-200',
};

const PRIORITY_STYLES = {
  'Low': 'bg-slate-100 text-slate-600',
  'Medium': 'bg-blue-50 text-blue-600',
  'High': 'bg-red-50 text-red-600',
};

const emptyTaskForm = { title: '', description: '', status: 'Open', assigned_to: '', due_date: '', priority: 'Medium' };

export const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Ticket status edit
  const [editingStatus, setEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  // Ticket assignee edit
  const [editingAssignee, setEditingAssignee] = useState(false);
  const [newAssignee, setNewAssignee] = useState('');
  const [assigneeSubmitting, setAssigneeSubmitting] = useState(false);
  const [serviceUsers, setServiceUsers] = useState([]);

  // Tasks
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [taskError, setTaskError] = useState('');
  const [deleteTaskConfirm, setDeleteTaskConfirm] = useState(null);

  // History modal
  const [historyTask, setHistoryTask] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchTicket();
  }, [id]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const [res, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tickets/${id}`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/users`, getAuthHeaders()),
      ]);
      setTicket(res.data);
      setServiceUsers((usersRes.data || []).filter(u => u.active && (u.role_name === 'Service Manager' || u.role_name === 'Service Technician')));
    } catch (err) { setError('Failed to load ticket'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (ticket) fetchTasks();
  }, [ticket]);

  const fetchTasks = async () => {
    setTasksLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tasks/by-ticket/${id}`, getAuthHeaders());
      setTasks(res.data);
    } catch (err) { /* silent */ }
    finally { setTasksLoading(false); }
  };

  // ---- Ticket Status ----
  const handleStatusSave = async () => {
    setStatusSubmitting(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/tickets/${id}`, { status: newStatus }, getAuthHeaders());
      setTicket(res.data);
      setEditingStatus(false);
    } catch (err) { /* silent */ }
    finally { setStatusSubmitting(false); }
  };

  // ---- Ticket Assignee ----
  const handleAssigneeSave = async () => {
    setAssigneeSubmitting(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/tickets/${id}`, { assigned_to: newAssignee || null }, getAuthHeaders());
      setTicket(res.data);
      setEditingAssignee(false);
    } catch (err) { /* silent */ }
    finally { setAssigneeSubmitting(false); }
  };

  // ---- Tasks ----
  const openTaskForm = (task = null) => {
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title || '', description: task.description || '',
        status: task.status || 'Open', assigned_to: task.assigned_to || '',
        due_date: task.due_date || '', priority: task.priority || 'Medium'
      });
    } else {
      setEditingTask(null);
      setTaskForm({ ...emptyTaskForm });
    }
    setTaskError('');
    setShowTaskForm(true);
  };

  const handleTaskFieldChange = (e) => {
    const { name, value } = e.target;
    setTaskForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    setTaskError('');
    if (!taskForm.title.trim()) { setTaskError('Title is required'); return; }
    setTaskSubmitting(true);
    try {
      const payload = { ...taskForm, ticket_id: id };
      ['description', 'assigned_to', 'due_date'].forEach(k => {
        if (!payload[k]?.trim()) payload[k] = null;
      });
      if (editingTask) {
        const { ticket_id, ...updatePayload } = payload;
        await axios.put(`${BACKEND_URL}/api/tasks/${editingTask.id}`, updatePayload, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/tasks`, payload, getAuthHeaders());
      }
      setShowTaskForm(false);
      fetchTasks();
    } catch (err) { setTaskError(err.response?.data?.detail || 'Failed to save task'); }
    finally { setTaskSubmitting(false); }
  };

  const handleQuickStatusChange = async (taskId, newTaskStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/tasks/${taskId}`, { status: newTaskStatus }, getAuthHeaders());
      fetchTasks();
    } catch (err) { /* silent */ }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${taskId}`, getAuthHeaders());
      setDeleteTaskConfirm(null);
      fetchTasks();
    } catch (err) { /* silent */ }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error) return (
    <div>
      <button onClick={() => navigate('/admin/services/tickets')} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Tickets
      </button>
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>
    </div>
  );
  if (!ticket) return null;

  return (
    <div data-testid="ticket-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <button onClick={() => navigate('/admin/services/tickets')} data-testid="back-btn"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-3 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Tickets
          </button>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">
            {ticket.ticket_number}
          </h1>
        </div>
      </div>

      {/* Ticket Info + Lead Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Ticket status card */}
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Ticket Info</h3>
          <div className="space-y-3">
            <div>
              <span className="block text-xs text-slate-500 uppercase mb-1">Status</span>
              {editingStatus ? (
                <div className="flex items-center gap-2">
                  <select value={newStatus} onChange={e => setNewStatus(e.target.value)} data-testid="ticket-status-select"
                    className="flex-1 px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
                    {TICKET_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={handleStatusSave} disabled={statusSubmitting} data-testid="save-ticket-status-btn"
                    className="px-3 py-1.5 bg-orange-500 text-white text-xs font-bold uppercase rounded-sm hover:bg-orange-600 disabled:opacity-50">
                    {statusSubmitting ? '...' : 'Save'}
                  </button>
                  <button onClick={() => setEditingStatus(false)} className="p-1 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium border ${STATUS_STYLES[ticket.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}
                    data-testid="ticket-status-badge">{ticket.status}</span>
                  <button onClick={() => { setNewStatus(ticket.status); setEditingStatus(true); }} data-testid="edit-ticket-status-btn"
                    className="p-1 text-slate-400 hover:text-orange-500"><Pencil className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
            <div>
              <span className="block text-xs text-slate-500 uppercase mb-1">Quotation</span>
              <span className="text-sm font-mono text-slate-900">{ticket.quotation_number || '-'}</span>
            </div>
            <div>
              <span className="block text-xs text-slate-500 uppercase mb-1">Created</span>
              <span className="text-sm text-slate-700">{formatDateTime(ticket.created_at)}</span>
            </div>
            {ticket.description && (
              <div>
                <span className="block text-xs text-slate-500 uppercase mb-1">Description</span>
                <p className="text-sm text-slate-700">{ticket.description}</p>
              </div>
            )}
            <div>
              <span className="block text-xs text-slate-500 uppercase mb-1">Assigned To</span>
              {editingAssignee ? (
                <div className="flex items-center gap-2">
                  <select value={newAssignee} onChange={e => setNewAssignee(e.target.value)} data-testid="ticket-assignee-select"
                    className="flex-1 px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
                    <option value="">-- None --</option>
                    {serviceUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role_name})</option>)}
                  </select>
                  <button onClick={handleAssigneeSave} disabled={assigneeSubmitting} data-testid="save-ticket-assignee-btn"
                    className="px-3 py-1.5 bg-orange-500 text-white text-xs font-bold uppercase rounded-sm hover:bg-orange-600 disabled:opacity-50">
                    {assigneeSubmitting ? '...' : 'Save'}
                  </button>
                  <button onClick={() => setEditingAssignee(false)} className="p-1 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${ticket.assigned_to_name ? 'font-medium text-orange-600' : 'text-slate-400'}`}>{ticket.assigned_to_name || 'None'}</span>
                  <button onClick={() => { setNewAssignee(ticket.assigned_to || ''); setEditingAssignee(true); }} data-testid="edit-ticket-assignee-btn"
                    className="p-1 text-slate-400 hover:text-orange-500"><Pencil className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lead info card */}
        <div className="bg-white rounded-sm border border-slate-200 p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Lead Information</h3>
            <button onClick={() => navigate(`/admin/services/leads/${ticket.lead_id}`)} data-testid="go-to-lead-btn"
              className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium">
              View Lead <ExternalLink className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-slate-50 rounded"><FileText className="h-4 w-4 text-slate-400" /></div>
              <div>
                <span className="block text-xs text-slate-500 uppercase mb-0.5">Name</span>
                <span className="text-sm font-medium text-slate-900">{ticket.lead_name || '-'}</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-slate-50 rounded"><Mail className="h-4 w-4 text-slate-400" /></div>
              <div>
                <span className="block text-xs text-slate-500 uppercase mb-0.5">Email</span>
                <span className="text-sm text-slate-900">{ticket.lead_email || '-'}</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-slate-50 rounded"><Phone className="h-4 w-4 text-slate-400" /></div>
              <div>
                <span className="block text-xs text-slate-500 uppercase mb-0.5">Phone</span>
                <span className="text-sm text-slate-900">{ticket.lead_phone || '-'}</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-slate-50 rounded"><Package className="h-4 w-4 text-slate-400" /></div>
              <div>
                <span className="block text-xs text-slate-500 uppercase mb-0.5">Product</span>
                <span className="text-sm text-slate-900">{ticket.lead_product_name || '-'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== TASKS SECTION ========== */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Tasks</h2>
          {!showTaskForm && (
            <button onClick={() => openTaskForm()} data-testid="add-task-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-5 py-2.5 rounded-none hover:bg-orange-600 transition-colors text-sm">
              <Plus className="h-4 w-4" /> Add Task
            </button>
          )}
        </div>

        {/* Task Form */}
        {showTaskForm && (
          <div className="bg-white rounded-sm border border-slate-200 mb-6">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
                {editingTask ? 'Edit Task' : 'New Task'}
              </h3>
              <button onClick={() => setShowTaskForm(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleTaskSubmit} className="p-6">
              {taskError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4"><AlertCircle className="h-5 w-5" /><span>{taskError}</span></div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title <span className="text-red-500">*</span></label>
                  <input type="text" name="title" value={taskForm.title} onChange={handleTaskFieldChange} required
                    data-testid="task-input-title"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea name="description" value={taskForm.description} onChange={handleTaskFieldChange} rows={2}
                    data-testid="task-input-description"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select name="status" value={taskForm.status} onChange={handleTaskFieldChange} data-testid="task-input-status"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                    {TASK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <select name="priority" value={taskForm.priority} onChange={handleTaskFieldChange} data-testid="task-input-priority"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                    {TASK_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                  <input type="date" name="due_date" value={taskForm.due_date} onChange={handleTaskFieldChange}
                    data-testid="task-input-due-date"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assigned To</label>
                  <select name="assigned_to" value={taskForm.assigned_to} onChange={handleTaskFieldChange}
                    data-testid="task-input-assigned-to"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                    <option value="">-- None --</option>
                    {serviceUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role_name})</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button type="button" onClick={() => setShowTaskForm(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={taskSubmitting} data-testid="submit-task-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {taskSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingTask ? 'Update Task' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tasks List */}
        {tasksLoading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
        ) : tasks.length === 0 && !showTaskForm ? (
          <div className="bg-white rounded-sm border border-slate-200 text-center py-12">
            <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 mb-4">No tasks yet for this ticket</p>
            <button onClick={() => openTaskForm()} className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium text-sm">
              <Plus className="h-4 w-4" /> Add First Task
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {tasks.map(task => (
              <div key={task.id} className="bg-white rounded-sm border border-slate-200" data-testid={`task-card-${task.id}`}>
                <div className="px-6 py-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-medium text-slate-900">{task.title}</h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[task.status] || 'bg-slate-100 text-slate-600'}`}>
                          {task.status}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_STYLES[task.priority] || ''}`}>
                          {task.priority}
                        </span>
                      </div>
                      {task.description && <p className="text-sm text-slate-600 mb-1">{task.description}</p>}
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        {task.assigned_to_name && <span>Assigned: {task.assigned_to_name}</span>}
                        {task.due_date && <span>Due: {formatDate(task.due_date)}</span>}
                        <span>Created: {formatDate(task.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Quick status change */}
                      <select value={task.status} onChange={e => handleQuickStatusChange(task.id, e.target.value)}
                        data-testid={`task-quick-status-${task.id}`}
                        className="px-2 py-1 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none bg-white">
                        {TASK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => setHistoryTask(task)} data-testid={`task-history-btn-${task.id}`}
                        className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors" title="View History">
                        <Clock className="h-4 w-4" />
                      </button>
                      <button onClick={() => openTaskForm(task)} data-testid={`task-edit-btn-${task.id}`}
                        className="p-1.5 text-slate-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors" title="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleteTaskConfirm(task.id)} data-testid={`task-delete-btn-${task.id}`}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Task Confirm */}
      {deleteTaskConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this task?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTaskConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleDeleteTask(deleteTaskConfirm)} data-testid="confirm-delete-task-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Task History Modal */}
      {historyTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Task History</h2>
              <button onClick={() => setHistoryTask(null)} className="p-2 text-slate-400 hover:text-slate-600" data-testid="close-history-modal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="mb-4">
                <h4 className="font-medium text-slate-900">{historyTask.title}</h4>
                <p className="text-xs text-slate-500">Current Status: {historyTask.status}</p>
              </div>

              {(!historyTask.history || historyTask.history.length === 0) ? (
                <p className="text-slate-500 text-sm py-4">No history available</p>
              ) : (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-slate-200"></div>

                  <div className="space-y-4">
                    {[...historyTask.history].reverse().map((entry, idx) => (
                      <div key={idx} className="relative pl-9" data-testid={`history-entry-${idx}`}>
                        {/* Timeline dot */}
                        <div className={`absolute left-1.5 top-1.5 w-3 h-3 rounded-full border-2 ${
                          entry.action === 'Created' ? 'bg-green-500 border-green-500' :
                          entry.action === 'Status Changed' ? 'bg-orange-500 border-orange-500' :
                          'bg-blue-500 border-blue-500'
                        }`}></div>

                        <div className="bg-slate-50 rounded-sm p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-900">{entry.action}</span>
                            <span className="text-xs text-slate-400">{formatDateTime(entry.timestamp)}</span>
                          </div>
                          {entry.action === 'Created' && (
                            <p className="text-xs text-slate-600">Task created with status <span className="font-medium">{entry.to_status}</span></p>
                          )}
                          {entry.action === 'Status Changed' && (
                            <p className="text-xs text-slate-600">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[entry.from_status] || 'bg-slate-100 text-slate-600'}`}>{entry.from_status}</span>
                              <span className="mx-1.5 text-slate-400">&rarr;</span>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[entry.to_status] || 'bg-slate-100 text-slate-600'}`}>{entry.to_status}</span>
                            </p>
                          )}
                          {entry.action === 'Priority Changed' && (
                            <p className="text-xs text-slate-600">
                              Priority: <span className="font-medium">{entry.old_value}</span> &rarr; <span className="font-medium">{entry.new_value}</span>
                            </p>
                          )}
                          {entry.action === 'Assignee Changed' && (
                            <p className="text-xs text-slate-600">
                              Assigned: <span className="font-medium">{entry.old_value}</span> &rarr; <span className="font-medium">{entry.new_value}</span>
                            </p>
                          )}
                          {entry.action === 'Due Date Changed' && (
                            <p className="text-xs text-slate-600">
                              Due Date: <span className="font-medium">{entry.old_value}</span> &rarr; <span className="font-medium">{entry.new_value}</span>
                            </p>
                          )}
                          {entry.action === 'Title Changed' && (
                            <p className="text-xs text-slate-600">
                              Title: <span className="font-medium">{entry.old_value}</span> &rarr; <span className="font-medium">{entry.new_value}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end">
              <button onClick={() => setHistoryTask(null)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
